/**
 * English strings of the product catalogue (Products & prices › Product catalogue, Server groups,
 * Locations, Service price calculator; ADR-026). The Vietnamese file has exactly the same keys;
 * every code the server returns has a name here (`tests/catalog.test.mjs`).
 */
export const catalog = {
  crumb: 'Products & prices',
  title: 'Product catalogue',
  subtitle: 'What Tino sells besides domains: hosting, cloud servers, email, websites, servers, licences and SSL. Every plan starts as a draft and goes on sale once it has prices, stock and (for automatic products) a way to be delivered.',
  groups: 'Product groups',
  allGroups: 'All groups',
  noProducts: 'No product in this group yet.',
  productCount_one: '{{count}} product',
  productCount_other: '{{count}} products',
  onSale_one: '{{count}} plan on sale',
  onSale_other: '{{count}} plans on sale',
  back: 'Back to the catalogue',
  backToProduct: 'Back to the product',
  draftNotice: 'Draft plans are not sold: customers do not see them.',
  fourEyesNotice: 'This is the production system: putting a plan on sale and publishing new prices need a second person to approve.',
  approvalRequested: 'Sent for approval. Once another person approves it (Home › Approvals), do the same action again to apply it.',
  applied: 'Done.',
  applyApproved: 'Apply (approved by a second person)',
  col: {
    product: 'Product',
    group: 'Group',
    delivery: 'Delivery',
    plans: 'Plans',
    visibility: 'Status',
    plan: 'Plan',
    cycles: 'Billing cycles',
    tax: 'VAT',
    fromPrice: 'From',
    ready: 'Ready to sell',
  },
  visibility: { DRAFT: 'Draft', PUBLIC: 'On sale', HIDDEN: 'Hidden (renewals only)', RETIRED: 'Retired' },
  fulfilment: { PROVISIONED: 'Automatic (through a module)', MANUAL: 'By staff' },
  branch: { dns: 'DNS', hosting: 'Hosting', email: 'Email', cloud: 'Cloud server', website: 'Website' },
  serviceLabel: { DOMAIN: 'Named by its domain', HOSTNAME: 'Named by its hostname', NONE: 'No name' },
  saleMode: { CYCLE: 'By billing cycle', HOURLY: 'By the hour' },
  cycle: { ONCE: 'One-time', H1: '1 hour', M1: '1 month', M3: '3 months', M6: '6 months', M12: '1 year', M24: '2 years', M36: '3 years', M48: '4 years', M60: '5 years' },
  discount: '−{{pct}}%',
  tax: { VAT8: 'VAT 8%', VAT10: 'VAT 10%', KCT: 'Not subject to VAT' },
  problem: {
    NO_STOCK: 'No location where it can be sold (stock closed or used up)',
    NO_FULFILMENT: 'No module connection yet: module, server group and package',
    NO_ACCEPTING_MEMBER: 'The server group has no server of this module taking new services',
    PLACEMENT_BRANCH_MISMATCH: 'The server group serves another kind of product',
    PRODUCT_RETIRED: 'The product is retired',
    NO_RETAIL_BOOK: 'There is no active retail price book',
    NO_PRICE: 'No price for a billing cycle',
    NO_TAX_RATE: 'The VAT rate of the plan is not in force',
    NO_TAX_CODE: 'Neither the plan nor its group has a VAT code',
    SALE_MODE_MISMATCH: 'The cycles of the plan do not fit how its group is sold',
  },
  productTabs: { info: 'Details', plans: 'Plans', options: 'Configuration options', addons: 'Add-ons', domains: 'Domains', lifecycle: 'Overdue handling', history: 'History' },
  planTabs: { specs: 'Specifications', prices: 'Prices per cycle', pricesHourly: 'Hourly prices', delivery: 'Module connection', stock: 'Stock & locations', domains: 'Free domains', alongside: 'Offered alongside' },
  info: {
    code: 'Code',
    group: 'Group',
    fulfilment: 'Delivered',
    branch: 'Kind of service',
    label: 'Service named by',
    trial: 'Free trial',
    trialDays_one: '{{count}} day',
    trialDays_other: '{{count}} days',
    moneyBack: 'Money-back period',
    none: 'None',
  },
  plans: {
    empty: 'This product has no plan yet.',
    notReady: 'Not ready to sell yet:',
    ready: 'Ready to sell',
    publish: 'Put on sale',
    unpublish: 'Take off sale',
    retire: 'Retire',
    publishTitle: 'Put {{plan}} on sale',
    unpublishTitle: 'Take {{plan}} off sale',
    publishDescription: 'Customers can order the plan at its prices once it is on sale. A plan taken off sale stays renewable for the services that already have it.',
    newPlan: 'New plan',
    taxFromGroup: 'from the group',
    noTax: 'No VAT code',
  },
  options: {
    empty: 'No configuration option: customers only choose the billing cycle.',
    kind: { SELECT: 'Pick one (list)', RADIO: 'Pick one (buttons)', CHECKBOX: 'Tick box', QUANTITY: 'Quantity', TEXT: 'Text', SSH_KEY: 'SSH key', HIDDEN: 'Set from the product page' },
    required: 'Required',
    changeable: 'Can change after purchase',
    mapsTo: 'Fills:',
    default: 'default',
    free: 'free',
    condition: 'When {{group}} is {{items}}: {{effect}} {{targets}}',
    effect: { HIDE: 'not available', REQUIRE: 'required' },
    price: '{{amount}} đ / {{cycle}}',
    setup: 'setup {{amount}} đ',
    percent: '+{{pct}}% of the hourly price',
    standardKinds: 'Standard',
    moduleKinds: 'Of {{module}}',
    mapsToNone: 'None: it only changes the price',
    toGroup: '→ server group {{group}}',
  },
  optionKey: {
    dedicated_ip: 'Dedicated IP address',
    image: 'Operating system template',
    ssh_keys: 'SSH keys',
    ipv4: 'More IPv4 addresses',
    ipv6: 'IPv6 address',
    template: 'Website template',
  },
  addons: {
    empty: 'No add-on for this product.',
    billing: { RECURRING: 'Billed with the service', ONCE: 'Once' },
    noPrice: 'No price yet',
  },
  lifecycle: {
    title: 'Overdue handling',
    intro: 'Like HostBill and WHMCS, each product has its own settings. A field left empty takes the group’s value, then the platform’s.',
    invoice_days_before: 'Renewal order (with its QR) issued',
    reminder_days: 'Reminders',
    payment_due_days_before: 'Pay-by date on the notice',
    suspend: 'Suspend (lock) the service',
    terminate: 'Terminate (cancel) the service',
    keep_backups_days: 'Last backup kept',
    late_fee: 'Late fee',
    auto_debit_wallet: 'Pay renewals from the wallet when it holds enough',
    daysBefore_one: '{{count}} day before the due date',
    daysBefore_other: '{{count}} days before the due date',
    daysOverdue_one: '{{count}} day overdue',
    daysOverdue_other: '{{count}} days overdue',
    days_one: '{{count}} day',
    days_other: '{{count}} days',
    off: 'Off',
    on: 'On',
    noLateFee: 'None',
    lateFee: '{{pct}}% of the renewal, {{days}} days overdue',
    source: { PRODUCT: 'this product', GROUP: 'its group', GLOBAL: 'platform default' },
    from: 'from {{source}}',
    calendar: 'Calendar (from the due date)',
    day: 'Day',
    event: { RENEWAL_ORDER: 'Renewal order and QR sent', REMINDER: 'Reminder', LATE_FEE: 'Late fee added', SUSPEND: 'Service suspended', TERMINATE: 'Service terminated, data deleted' },
    dueDay: 'due date',
    edit: 'Change for this product',
    editTitle: 'Overdue handling of {{product}}',
    emptyInherits: 'Leave a field empty to keep the group’s or the platform’s value.',
    reminderHint: 'Days before the due date, separated by commas; 0 is the due date, −3 is three days overdue.',
    problem: {
      terminateBeforeSuspend: 'Termination must come after suspension.',
      payByBeforeOrder: 'The pay-by date cannot come before the renewal order.',
      reminderBeforeOrder: 'A reminder cannot come before the renewal order.',
    },
  },
  history: { empty: 'No change recorded yet.', what: 'What', who: 'Who', why: 'Why', when: 'When', system: 'System' },
  specs: { empty: 'No specification yet.' },
  prices: {
    intro: 'Prices are before VAT, for the whole cycle. A new price applies from the moment chosen; quotes already given keep theirs. Renewals are charged at the price in force on the renewal day.',
    introHourly: 'Prices are before VAT, for one hour, with no setup fee. A new price applies from the moment chosen; quotes already given keep theirs.',
    book: 'Price book',
    current: 'In force',
    next: 'From {{date}}: {{amount}} đ',
    none: 'No price',
    setup: 'Setup {{amount}} đ',
    edit: 'New prices',
    editTitle: 'New prices of {{plan}}',
    from: 'Applies from',
    fromHint: 'Empty: now. A price never changes the past.',
    amount: 'Price of the cycle (đ, before VAT)',
    setupFee: 'Setup fee (đ)',
    hourly: 'Hourly price',
    hourlyAmount: 'Price of one hour (đ, before VAT)',
    unreadable: 'Enter whole đồng, for example 758.400.',
    bookKind: { TIER: 'Tier', CUSTOMER_CONTRACT: 'Customer contract' },
  },
  delivery: {
    manual: 'Staff deliver this product by hand: no module is called.',
    none: 'No module connection yet.',
    module: 'Module',
    group: 'Server group',
    providerPlan: 'Package at the provider',
    providerPlanHint: 'The WHM package, the Proxmox flavor or the WildDuck plan.',
    providerPlanListed: 'Read from every server of the group taking new services: a package missing on one of them cannot be picked.',
    noProviderPlan: 'None (the module has no package list)',
    template: 'Template',
    templateHint: 'Only when the plan always installs one (an application VPS).',
    overrides: 'Settings of this plan',
    noOverrides: 'All from the server group',
    noModuleAccess: 'Seeing the connections needs the right to read module settings: the package and the settings are typed, and checked when saved.',
    noGroup: 'No server group serves this module yet: add one under Server groups.',
    settingsHint: 'Empty: the value of the server group. Type a value only where this plan differs.',
    step: { module: '1. Module', group: '2. Server group', plan: '3. Package at the provider', settings: '4. Placement settings of this plan' },
    edit: 'Edit module connection',
    editTitle: 'Module connection of {{plan}}',
    accepting: 'Taking new services',
    notAccepting: 'Not taking new services',
  },
  stock: {
    none: 'No location yet: the plan cannot be sold.',
    location: 'Location',
    mode: { UNLIMITED: 'Unlimited', COUNTED: 'Counted', CAPACITY: 'While servers have room', CLOSED: 'Out of stock' },
    left: '{{left}} left of {{quantity}}',
    customerChoice: 'Customers may choose it',
    edit: 'Change',
    editTitle: 'Stock of {{plan}} at {{location}}',
    quantity: 'Units',
    add: 'Add a location',
  },
  domains: {
    none: 'No free domain with this plan.',
    rule: '.{{zone}} free from {{cycle}}',
    renewals: 'renewals free too',
    firstTerm: 'first term only',
  },
  alongside: { none: 'Nothing offered alongside.', intro: 'Offered next to this plan in the cart, each bought as its own service.' },
  domainOptions: {
    intro: 'As the Domains tab of HostBill: which domain a service uses. Today only a domain the customer has, and only one registered with Tino, take effect (when staff create a service); the rest is stored for the cart.',
    edit: 'Edit',
    editTitle: 'Domains of {{product}}',
    own: 'A domain the customer has',
    ownAny: 'Any domain',
    ownHere: 'Only one registered with Tino',
    register: 'New registration',
    transfer: 'Transfer in',
    tlds: 'Extensions offered',
    allTlds: 'Every extension on sale',
    subdomain: 'Free subdomain',
    free: 'Free domains by plan',
    freeIntro: 'Each plan sets its free domains in its Free domains tab.',
  },
  order: {
    button: 'New service',
    title: 'New service for {{customer}}',
    description: "Order a plan on sale at the customer's prices. The total is held on the customer's wallet; the service is provisioned and invoiced once it is active.",
    product: 'Product',
    plan: 'Plan',
    cycle: 'Cycle',
    location: 'Location',
    domain: "The service's domain",
    domainHint: 'The domain of the hosting, website or e-mail account, or of the DNS zone.',
    username: 'cPanel username',
    usernameHint: 'Optional: checked at once against the hosting panel\'s rules (cPanel: up to 8 lower-case letters and digits, starting with a letter, not "test…", no reserved name) and against the accounts it has. Empty: made from the domain.',
    usernameChecking: 'Checking the username on the plan\'s hosting connection…',
    label: 'Service name',
    labelHint: 'Optional; helps the customer and staff recognise the service.',
    promotion: 'Promotion code',
    choose: 'Choose…',
    price: 'Show price',
    total: 'Total {{total}} (VAT {{tax}})',
    walletNote: "The order is paid from {{customer}}'s wallet. A wallet short of the total refuses the order and says how much to top up.",
    submit: 'Place order from wallet',
    placed: 'Order {{description}} placed; {{total}} held on the wallet. The service is being provisioned.',
    options: 'Configurable options',
    optionDefault: 'Default: {{item}}',
    optionHidden: '{{item}} (staff only)',
    quantityHint: 'From {{min}} to {{max}}; empty: {{min}}.',
  },
  servers: {
    members: 'Group servers',
    membersTitle: 'Servers of {{group}}',
    noMembers: 'The group has no server yet: plans delivered here cannot create a new service.',
    add: 'Add a server',
    addButton: 'Add',
    chooseConnection: 'Choose a module connection',
    serverHint: 'The server or node of the connection (hosting, VPS); leave empty when the connection chooses.',
    remove: 'Remove from the group',
    duplicate: 'This server is already in the group.',
    title: 'Server groups',
    subtitle: 'Where a new service of a plan is created: the module’s instances and, for hosting and cloud, their servers or nodes.',
    empty: 'No server group yet.',
    strategy: { LEAST_USED: 'Least used first', FILL_FIRST: 'Fill one, then the next', FIXED: 'Always the same' },
    instance: 'Instance',
    server: 'Server',
    weight: 'Weight',
    plans_one: '{{count}} plan delivered here',
    plans_other: '{{count}} plans delivered here',
    location: 'Location',
    toggle: 'Stop / resume new services',
    toggleTitle: 'Servers of {{group}} taking new services',
    moduleLocked: 'The group has servers or plans: its module cannot change.',
    noServerData: 'No report from this connection yet: refresh its servers, or leave it empty to let the connection choose.',
    defaults: 'Placement settings of the group',
    defaultsHint: 'Used by every plan of this group; a plan may override those it is allowed to.',
  },
  fulfil: {
    title: 'Ready to sell',
    intro: 'Whether the servers can take this plan: checked from their last data when saving and putting on sale.',
    check: 'Check',
    checkLive: 'Check now',
    checked: { CACHED: 'Checked from the servers’ last data, {{when}}.', LIVE: 'Checked by asking every server, {{when}}.' },
    ready: 'Ready',
    notReady: 'Not ready',
    state: { OK: 'Passed', WARN: 'Warning', BLOCK: 'Blocks sale' },
    condition: {
      module: 'The server group serves this module',
      members: 'The group has its connections, switched on',
      facts: 'Data from the servers',
      plan: 'The package is on every server',
      settings: 'Placement settings',
      options: 'Values of configurable options are on the servers',
      capacity: 'Room on the servers',
    },
    problem: {
      GROUP_MODULE_MISMATCH: 'The server group uses another module',
      MEMBER_NOT_FOUND: 'A connection of the group no longer exists',
      MEMBER_DISABLED: 'A connection of the group is switched off',
      FIXED_NEEDS_ONE: '“Always the same” needs exactly one server taking new services',
      FACTS_MISSING: 'No data read from this server yet: press Refresh on the server group',
      FACTS_STALE: 'The data of this server is more than 24 hours old',
      NO_SERVER_DATA: 'The server has not reported its accounts yet',
      NOT_SENT: 'The server could not be reached: nothing was asked',
      UNKNOWN: 'The server did not answer in time',
      REFUSED: 'The server refused to be read (its reason follows)',
      PLAN_NOT_FOUND: 'The package is not on this server',
      SETTINGS_INVALID: 'The settings, merged with the group’s, are not valid for the module',
      NOT_OVERRIDABLE: 'The plan overrides a setting it may not',
      VALUE_NOT_FOUND: 'A value of a configurable option is not on this server',
      NO_CAPACITY: 'The server has no room for a new account',
    },
    fix: { edit: 'Edit', group: 'Open the server groups', options: 'Open configurable options' },
  },
  connection: {
    instance: 'Connection',
    server: 'Server',
    anyServer: 'The connection chooses',
    state: 'State',
    accounts: 'Accounts / capacity',
    accountsOf: '{{accounts}} / {{max}}',
    accountsFull: '{{accounts}} · full',
    serverDown: 'server not answering',
    facts: 'Data from the server',
    factsAge: 'read {{age}}',
    noFacts: 'no data yet',
    factsError: 'could not be read',
    refresh: 'Refresh',
    choose: 'Choose…',
    missingOn: 'missing on {{servers}}',
    notListed: '{{value}} (not listed by the servers)',
    noList: 'No list yet: press Refresh on the server group, or check the group has a connection taking new services.',
    fromGroup: 'From the group',
    fromGroupValue: 'From the group: {{value}}',
    notOverridable: 'A plan cannot override this: it always comes from the server group.',
    yes: 'Yes',
    no: 'No',
  },
  locations: {
    title: 'Locations',
    subtitle: 'Data centres where plans are sold.',
    empty: 'No location yet.',
    code: 'Code',
    name: 'Name',
    city: 'City',
    active: 'In use',
    inactive: 'Not in use',
    add: 'Add a location',
  },
  quote: {
    title: 'Service price calculator',
    subtitle: 'The price of a plan for a billing cycle, line by line with VAT, as a customer would pay it. Nothing is saved.',
    product: 'Product',
    plan: 'Plan',
    cycle: 'Billing cycle',
    book: 'Price book',
    action: 'For',
    new: 'A new service',
    renew: 'A renewal',
    promotion: 'Promotion code',
    run: 'Calculate',
    line: 'Line',
    amount: 'Amount',
    vat: 'VAT',
    total: 'Total',
    subtotal: 'Before VAT',
    payToday: 'To pay today',
    renewal: 'Next renewal (before VAT, today’s prices)',
    discount: 'discount {{amount}} đ',
    kind: { PLAN: 'Plan', SETUP: 'Setup fee', OPTION: 'Option', ADDON: 'Add-on' },
    pick: 'Pick a product and a plan.',
  },
  editor: {
    noTaxRate: 'No VAT rate yet: add one under Pricing → Tax rates',
    groupTaxNone: 'Not set',
    taxFromGroup: 'From the group ({{tax}})',
    saleModeLocked: 'The group has products: how it is sold cannot change.',
    hourlyPlan: 'The group sells by the hour: the plan has one hourly price. It can be quoted, not ordered yet.',
    unreadablePercent: 'Type a percent, for example 5 or 12.5.',
    taxRateMissing: 'no rate in force',
    newGroup: 'New group',
    newGroupTitle: 'New product group',
    editGroup: 'Edit group',
    editGroupTitle: 'Edit group {{group}}',
    newProduct: 'New product',
    newProductTitle: 'New product',
    editProduct: 'Edit product',
    editProductTitle: 'Edit {{product}}',
    productVisibility: 'Show or hide',
    productVisibilityTitle: 'Show or hide {{product}}',
    productVisibilityIntro: 'A product is shown in the catalogue once one of its plans is on sale. Retiring a product needs its plans off sale first.',
    newPlanTitle: 'New plan of {{product}}',
    editPlan: 'Edit plan',
    editPlanTitle: 'Edit {{plan}}',
    addSpec: 'Add a line',
    remove: 'Remove',
    newOptionGroup: 'New choice',
    newOptionGroupTitle: 'New configuration choice of {{product}}',
    editOptionGroup: 'Edit',
    editOptionGroupTitle: 'Edit {{group}}',
    addCondition: 'Add a condition',
    noConditions: 'No condition: the choices of this group never depend on another one.',
    conditionSentence: 'When the customer picks {{items}} in {{group}}, these choices here {{effect}}: {{targets}}',
    anyItem: 'any choice',
    newItem: 'Add a choice',
    newItemTitle: 'New choice in {{group}}',
    editItem: 'Edit',
    editItemTitle: 'Edit {{item}}',
    setPrice: 'Set price',
    optionPriceTitle: 'Price of {{item}}',
    addonPriceTitle: 'Price of {{addon}}',
    perUnit: 'For a quantity, the price is per unit.',
    newAddon: 'New add-on',
    newAddonTitle: 'New add-on',
    editAddon: 'Edit',
    editAddonTitle: 'Edit {{addon}}',
    editCrossSells: 'Edit suggestions',
    crossSellsTitle: 'Sold alongside {{plan}}',
    editFreeDomains: 'Edit free domains',
    freeDomainsTitle: 'Free domains with {{plan}}',
    addFreeDomain: 'Add an extension',
    endPrice: 'End a price',
    endPriceTitle: 'End a price of {{plan}}',
    endPriceRow: '{{book}} · {{cycle}} · {{amount}} đ',
    endPriceNone: 'No price to end.',
    addStock: 'Add a location',
    addStockTitle: 'Sell {{plan}} at another location',
    clearLifecycle: 'Back to the group’s settings',
    clearLifecycleTitle: 'Overdue handling of {{product}} as its group',
    clearLifecycleIntro: 'Every setting of this product is removed: it follows its group, then the system defaults.',
    editLocation: 'Edit',
    editLocationTitle: 'Edit {{location}}',
    newServerGroup: 'New server group',
    editServerGroup: 'Edit',
    editServerGroupTitle: 'Edit {{group}}',
    none: '— None —',
    allPlans: 'Every plan of the product',
    anyCycle: 'Every cycle',
    codeInvalid: 'Use lower-case letters, digits and dashes.',
    unreadable: 'Type whole đồng, e.g. 758.400.',
    cyclesProblem: { none: 'Pick at least one billing cycle.', onceAlone: 'A one-time plan offers no other cycle.', hourAlone: 'An hourly plan offers no other cycle.' },
    conditionProblem: {
      unknownGroup: 'A condition names a choice the product does not have.',
      noTrigger: 'A condition needs at least one triggering choice.',
      badEffect: 'A condition needs what happens.',
    },
    quantityProblem: 'The lowest number must not be greater than the highest.',
    itemVisibility: { PUBLIC: 'Customers can pick it', HIDDEN: 'Hidden (staff only)', RETIRED: 'Withdrawn' },
    addonFulfilment: { MODULE_OP: 'Done automatically on the service', MANUAL: 'Done by staff', NONE: 'Nothing to do' },
    freeOperation: { REGISTER: 'New registration', TRANSFER: 'Transfer to Tino' },
    field: {
      code: { label: 'Code', help: 'Lower-case letters, digits and dashes. Given once, it never changes: it appears in links and reports.' },
      optionCode: { label: 'Code', help: 'Lower-case letters, digits, dashes and underscores. Given once, it never changes.' },
      nameVi: { label: 'Name in Vietnamese', help: 'What customers see on the Vietnamese site and on invoices.' },
      nameEn: { label: 'Name in English', help: 'What customers see on the English site. Left empty, the Vietnamese name is used.' },
      descriptionVi: { label: 'Description in Vietnamese', help: 'A short sentence shown under the name. Optional.' },
      descriptionEn: { label: 'Description in English', help: 'The same sentence in English. Optional.' },
      sort: { label: 'Display order', help: 'Smaller numbers come first.' },
      group: { label: 'Product group', help: 'The tile the product is listed under.' },
      fulfilment: { label: 'How the service is delivered', help: 'Automatically by a connected system, or by staff by hand.' },
      branch: { label: 'Kind of service', help: 'Decides which connected systems and server groups can deliver it. It cannot change once the product has a plan.' },
      serviceLabel: { label: 'The service is named by', help: 'What the customer types when buying: a domain, a server name, or nothing.' },
      trialDays: { label: 'Free trial (days)', help: 'From 1 to 30 days. Empty: no free trial.' },
      moneyBackDays: { label: 'Money-back period (days)', help: 'From 1 to 90 days. Empty: no refund period.' },
      productVisibility: { label: 'Status', help: 'Shown, hidden (existing services still renew) or withdrawn.' },
      cycles: { label: 'Billing cycles on sale', help: 'Every cycle needs a retail price before the plan goes on sale.' },
      taxCode: { label: 'VAT rate', help: 'The rate printed on the invoice for this add-on.' },
      saleMode: { label: 'How it is sold', help: 'By billing cycle (months, years) or by the hour. Fixed once the group has a product.' },
      groupTax: { label: 'VAT rate of the group', help: 'Plans that pick no rate of their own use this one. A change is refused while plans use it: give those plans their own rate first.' },
      planTax: { label: 'VAT rate', help: 'From the group: the plan uses the rate of its group. Pick a code only when the plan needs its own rate.' },
      pricePercent: { label: 'Extra (%)', help: 'A percent of the hourly price of the plan, before promotions. For example 5 or 12.5.' },
      upgradesTo: { label: 'Can be upgraded to', help: 'Plans of the same product a customer can move up to.' },
      specs: { label: 'Specifications', help: 'Lines shown on the price table, in this order.' },
      specLabelVi: { label: 'Name (Vietnamese)', help: 'For example “Dung lượng NVMe”.' },
      specLabelEn: { label: 'Name (English)', help: 'For example “NVMe storage”.' },
      specValue: { label: 'Value', help: 'For example “5 GB”.' },
      specUnit: { label: 'Unit', help: 'Optional, shown after the value.' },
      optionKind: { label: 'Field type', help: 'A standard type, or a type of a module the plans use. Cannot change later: create another option for a different type.' },
      required: { label: 'The customer must choose', help: 'Unticked: the customer may leave it empty.' },
      minQty: { label: 'Lowest number', help: 'The smallest quantity a customer can order.' },
      maxQty: { label: 'Highest number', help: 'The largest quantity a customer can order.' },
      step: { label: 'In steps of', help: 'For example 1, or 2 to sell pairs. Empty: 1.' },
      changeable: { label: 'Can be changed after purchase', help: 'The customer can change this choice on a running service.' },
      mapsTo: { label: 'Setting it fills when the service is created', help: 'A standard setting of this kind of service, or a placement setting of a module the plans use. None: the choice only changes the price.' },
      optionPlans: { label: 'Offered with', help: 'None ticked: every plan of the product.' },
      conditions: { label: 'Conditions', help: 'A choice made elsewhere can hide choices here or make one of them compulsory.' },
      whenGroup: { label: 'When the customer picks in', help: 'Another choice of the same product.' },
      whenItems: { label: 'one of', help: 'At least one.' },
      effect: { label: 'then here', help: 'Hidden choices cannot be picked; a required one must be.' },
      effectItems: { label: 'these choices', help: 'None ticked with “must be chosen”: any choice of this group.' },
      itemProviderValue: { label: 'Value sent when the service is created', help: 'For example the template name of an operating system. Empty: nothing is sent.' },
      itemServerValue: { label: 'Value on the servers', help: 'Exactly as the servers list it; checked on every server of the group when saved.' },
      itemPlacementGroup: { label: 'Server group', help: 'A service with this choice is created in this group. Only the groups of the same module.' },
      itemDefault: { label: 'Picked by default', help: 'At most one per group: ticking it unticks the others.' },
      itemVisibility: { label: 'Status', help: 'Hidden choices can only be picked by staff; withdrawn ones by nobody.' },
      addonBilling: { label: 'Billing', help: 'Billed with the service each cycle, or once.' },
      addonFulfilment: { label: 'What happens when bought', help: 'Done automatically on the service, by staff, or nothing.' },
      addonAction: { label: 'Automatic action', help: 'What the system does on the service, for example turning on backups.' },
      addonProducts: { label: 'Offered with', help: 'The products whose customers can add it.' },
      addonVisibility: { label: 'Status', help: 'Only “On sale” add-ons can be bought.' },
      priceBook: { label: 'Price book', help: 'A tier’s prices for every product, or one customer’s own prices.' },
      priceFrom: { label: 'Applies from', help: 'Empty: now. A new price never changes the past.' },
      priceAmount: { label: 'Price per cycle (đ, before VAT)', help: 'Whole đồng; 0 is free.' },
      setupFee: { label: 'Setup fee (đ)', help: 'Charged once, with the first cycle.' },
      upgradeFee: { label: 'Fee to choose it later (đ)', help: 'Charged when a running service switches to this choice.' },
      downgradeFee: { label: 'Fee to drop it (đ)', help: 'Charged when a running service leaves this choice.' },
      crossSellPlans: { label: 'Suggested in the cart', help: 'Plans of other products offered next to this plan; each is its own service.' },
      freeZone: { label: 'Extension', help: 'A domain of this extension is free with the plan.' },
      freeMinCycle: { label: 'When bought for at least', help: 'Shorter cycles pay for the domain.' },
      freeOperations: { label: 'Free for', help: 'A new registration, a transfer, or both.' },
      freeRenewals: { label: 'Free renewals too', help: 'Unticked: only the first term is free.' },
      endPrice: { label: 'Price to end', help: 'The price in force or a scheduled one.' },
      endAt: { label: 'Ends at', help: 'Empty: now. The plan cannot be sold for that cycle until a new price is set.' },
      locationCode: { label: 'Code', help: 'Lower-case letters, digits and dashes, given once.' },
      locationCity: { label: 'City', help: 'Shown to customers who choose where their service runs.' },
      locationActive: { label: 'In use', help: 'Unticked: no new service is sold at this location.' },
      stockLocation: { label: 'Location', help: 'A data centre where the plan is not sold yet.' },
      stockMode: { label: 'Availability', help: 'Unlimited, a counted quantity, while servers have room, or sold out.' },
      stockQuantity: { label: 'Quantity', help: 'How many can be sold here in total.' },
      serverBranch: { label: 'Kind of service', help: 'Plans of this kind of service can be created here.' },
      serverStrategy: { label: 'Which server first', help: 'How a new service picks among the group’s servers.' },
      serverLocation: { label: 'Location', help: 'The data centre of these servers. Optional.' },
      serverModule: { label: 'Module', help: 'Every server of the group is a connection of this module, and every plan delivered here uses it. Fixed once the group has servers or plans.' },
      ruleBook: { label: 'Price book', help: 'The prices of one tier, or one customer’s own prices.' },
      ruleScope: { label: 'Applies to', help: 'A whole group, a product, or a single plan.' },
      ruleTarget: { label: 'Which one', help: 'The more specific rule wins: plan before product before group.' },
      ruleCycle: { label: 'Billing cycle', help: 'Empty: every cycle. A fixed price is for one cycle.' },
      ruleType: { label: 'How the price is set', help: 'A discount off the retail price, or a fixed price for one plan.' },
      ruleValuePercent: { label: 'Discount (%)', help: 'From 0 to 100, off the retail price in force.' },
      ruleValueAmount: { label: 'Price (đ, before VAT)', help: 'Whole đồng for one cycle.' },
      rulePriority: { label: 'Priority', help: 'Between rules for the same thing, the smaller number wins. Usually 100.' },
      ruleFrom: { label: 'Applies from', help: 'Empty: now. The rule in force for the same thing ends then.' },
      ruleEnd: { label: 'Ends at', help: 'Empty: now.' },
      promoScope: { label: 'The code applies to', help: 'Domains as before, or a group, a product or a plan of the catalogue.' },
      promoTarget: { label: 'Which one', help: 'Only services of this group, product or plan get the discount.' },
      promoCycles: { label: 'Billing cycles', help: 'None ticked: every cycle.' },
      promoActions: { label: 'For', help: 'None ticked: new services only.' },
      promoRecurring: { label: 'Discount every renewal', help: 'Unticked: the first term only.' },
      promoOncePerCustomer: { label: 'Once per customer', help: 'A customer can use the code once.' },
      promoIncludeSetup: { label: 'Discount the setup fee too', help: 'Unticked: setup fees are paid in full.' },
      domainOwn: { label: 'Allow a domain the customer has', help: 'The customer names a domain of their own for the service. A product named by its domain with this off cannot be ordered by staff yet: there is no cart to register or transfer a domain.' },
      domainOwnHere: { label: 'Only a domain registered with Tino', help: 'The domain must be managed in this system; a staff order naming another domain is refused.' },
      domainRegister: { label: 'Offer a new registration', help: 'The customer registers a new domain with the service. Takes effect with the cart.' },
      domainTransfer: { label: 'Offer a transfer in', help: 'The customer moves a domain held elsewhere with the service. Takes effect with the cart.' },
      domainTlds: { label: 'Extensions offered', help: 'Separated by commas, for example vn, com.vn, com. Empty: every extension on sale.' },
      domainSubdomain: { label: 'Free subdomain', help: 'The suffix given to the customer, for example .tino.page. Empty: none. Stored only for now.' },
    },
  },
  rules: {
    title: 'Tier and customer prices',
    subtitle: 'Prices of the tier books and of customers’ own prices: a discount off the tier or standard price for a group, a product or a plan, or a fixed price for one plan. A new rule replaces the one in force for the same thing from its start.',
    book: 'Price book',
    allBooks: 'All books',
    showClosed: 'Show ended rules',
    empty: 'No rule: these books sell at the standard price.',
    col: { book: 'Book', appliesTo: 'Applies to', cycle: 'Cycle', rule: 'Price', priority: 'Priority', when: 'In force', reason: 'Reason' },
    scope: { GROUP: 'Group', PRODUCT: 'Product', PLAN: 'Plan' },
    type: { DISCOUNT_PERCENT: 'Percentage discount', FIXED_PRICE: 'Fixed price' },
    discount: '−{{value}}%',
    fixed: '{{amount}} đ',
    since: 'from {{date}}',
    until: 'until {{date}}',
    add: 'New rule',
    addTitle: 'New price rule',
    end: 'End',
    endTitle: 'End the rule for {{target}}',
    fixedNeedsPlan: 'A fixed price is for one plan and one cycle.',
    contract: 'Own prices of {{name}}',
  },
  promoScope: {
    title: 'Service promotions',
    subtitle: 'Which promotion codes give a discount on hosting, servers, email and the other services, and on what terms. A code that has been used keeps its terms: create a new code.',
    empty: 'No promotion code.',
    col: { code: 'Code', discount: 'Discount', valid: 'Valid', used: 'Used', appliesTo: 'Applies to', terms: 'Terms' },
    scope: { TLD: 'Domains', GROUP: 'Group', PRODUCT: 'Product', PLAN: 'Plan' },
    action: { NEW: 'New services', RENEW: 'Renewals', UPGRADE: 'Upgrades' },
    percent: '−{{value}}%',
    fixed: '−{{amount}} đ',
    used: '{{count}} / {{max}}',
    unlimited: 'no limit',
    edit: 'Set what it applies to',
    editTitle: 'What code {{code}} applies to',
    blocked: {
      disabled: 'The code is turned off.',
      used: 'The code has been used: its terms stay as they are. Create a new code.',
      domain: 'A domain code (with an extension or an operation) cannot apply to services.',
    },
    newOnly: 'New services only',
    everyCycle: 'every cycle',
    recurring: 'every renewal',
    firstOnly: 'first term only',
    oncePerCustomer: 'once per customer',
    includeSetup: 'setup fee included',
    disabled: 'Turned off',
  },
  reason: 'Reason',
  save: 'Save',
};
